/**
 * Resolve ONE cohort by id, through the global list's `cohortId` filter.
 *
 * The counterpart to `clients/use-client.ts`, and it exists for the same
 * reason: a picker that searches remotely cannot render the SELECTED row's
 * label from its result page, because the selection need not be on it. Ask
 * for it by id instead.
 *
 * This is also what makes the per-client cohort list boundable at all. A
 * cap on that endpoint drops the newest cohorts (it sorts soonest-start
 * first), so a preselected cohort past the boundary would vanish from its
 * `<Select>` — the picker resolving its own selection is the thing that
 * stops a ceiling from silently changing what a form points at.
 */
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '../api/query-keys'
import { adminCohortClient } from './api'
import type { CohortRow, ListCohortsParams } from './types'

/** The by-id lookup's params. Exported so callers can seed its cache. */
export function cohortByIdParams(
  clientId: string,
  cohortId: string,
): ListCohortsParams {
  // Client-scoped as well as id-scoped: the id alone would be answerable
  // across tenants, and every caller already knows which client it means.
  return { clientId, cohortIds: [cohortId] }
}

/**
 * `data` is the cohort, or `null` once the lookup resolved and found
 * nothing (a soft-deleted cohort an old link still points at).
 */
export function useCohort(clientId: string, cohortId: string) {
  const params = cohortByIdParams(clientId, cohortId)
  return useQuery({
    queryKey: queryKeys.cohortList(params),
    queryFn: () => adminCohortClient.listAll(params),
    enabled: clientId !== '' && cohortId !== '',
    select: (data): CohortRow | null => data.cohorts[0] ?? null,
  })
}
